import {
  AggregationType,
  getAggregationDrawerOptions,
  getMarketAccountDrawerOptions,
  MarketAccountStatusEnum,
  MarketAccountTypeEnum,
  ModeEnum,
  type Aggregation,
  type AggregationWithAccounts,
  type ColumnDef,
  type ExtendedAggregationForEA,
  type InputsAndDropdownsDrawerOption,
  type MarketAccount,
  type PricingRule,
} from '@talos/kyoko';
import type { ColDef } from 'ag-grid-community';
import { compact } from 'lodash-es';

export const CUSTOMER_AGGREGATIONS_COLUMNS: ColumnDef<ExtendedAggregationForEA>[] = [
  { field: 'Aggregation', type: 'text', initialHide: true },
  { field: 'Market', type: 'market' },
  { field: 'MarketAccount', type: 'marketAccount', initialHide: true },
  { field: 'UpdateAction', type: 'text', initialHide: true },
];

export const CUSTOMER_AGGREGATIONS_GROUP_COLUMN_DEF: ColDef<ExtendedAggregationForEA> = {
  headerName: 'Aggregation',
  sort: 'asc',
  pinned: 'left',
  valueFormatter: ({ context, value }) => {
    const foundAggregationName = context.current.aggregationsByName?.get(value)?.DisplayName;
    const foundMarketAccountName = context.current.marketAccountsByName?.get(value)?.DisplayName;
    const foundMarketName = context.current.marketDisplayNameByName?.get(value);
    return foundAggregationName ?? foundMarketAccountName ?? foundMarketName ?? value;
  },
};

export function getCustomerAggregationMarketsDrawerOptions(
  aggregations: Pick<Aggregation, 'Name' | 'DisplayName'>[],
  marketAccounts: Pick<MarketAccount, 'Name' | 'DisplayName' | 'Market'>[],
  marketDisplayNameByName?: Map<string, string>
): InputsAndDropdownsDrawerOption<ExtendedAggregationForEA>[] {
  return [
    {
      type: 'dropdown',
      field: 'Aggregation',
      title: 'Aggregation',
      getIsRequired: () => true,
      getDropdownOptions: () => getAggregationDrawerOptions(aggregations),
    },
    {
      type: 'dropdown',
      field: 'MarketAccount',
      title: 'Market Account',
      getIsRequired: () => true,
      getDropdownOptions: () => getMarketAccountDrawerOptions(marketAccounts, marketDisplayNameByName),
    },
  ];
}

export function getCustomerAggregationsDeletePath(entity: ExtendedAggregationForEA): string {
  return `/aggregations/${encodeURIComponent(entity.Aggregation)}`;
}

function isAggregationMarketRow(e: ExtendedAggregationForEA): boolean {
  return e.MarketAccount != null;
}

export function getCustomerAggregationsPatchPath(entity: ExtendedAggregationForEA): string {
  return isAggregationMarketRow(entity) ? `/aggregations/markets` : `/aggregations`;
}

export function getCustomerAggregationsPostPath(entity: ExtendedAggregationForEA): string {
  return isAggregationMarketRow(entity) ? `/aggregations/markets` : `/aggregations`;
}

export const CUSTOMER_AGGREGATIONS_ALLOW_MODE_SWITCH = {
  isDisabled: (e: ExtendedAggregationForEA) => !isAggregationMarketRow(e),
  isHidden: (e: ExtendedAggregationForEA) => !isAggregationMarketRow(e),
};

export function getCustomerAggregationsResponseAsBlotterEntity(response: unknown): ExtendedAggregationForEA {
  if (typeof response === 'object' && response !== null && 'Name' in response && typeof response.Name === 'string') {
    // Create an "Ext" row for the blotter grid - entityIDField is "Aggregation"
    return { Aggregation: response.Name, ...response } satisfies ExtendedAggregationForEA;
  }
  return response as ExtendedAggregationForEA;
}

const CUSTOMER_AGGREGATION_DELETE_IN_USE_TOOLTIP =
  'This aggregation is in use by a Pricing Rule and cannot be deleted.';
const CUSTOMER_AGGREGATION_DELETE_LOADING_TOOLTIP = 'Delete is unavailable until pricing rules finish loading.';

export function getCustomerAggregationsAllowDeleteEntity(getInUseAggregationNames: () => Set<string> | undefined) {
  return {
    isDisabled: (e: ExtendedAggregationForEA) => {
      const inUseAggregationNamesSet = getInUseAggregationNames();
      return (
        isAggregationMarketRow(e) || inUseAggregationNamesSet == null || inUseAggregationNamesSet.has(e.Aggregation)
      );
    },
    isDisabledTooltip: () => {
      const inUseAggregationNamesSet = getInUseAggregationNames();
      return inUseAggregationNamesSet == null
        ? CUSTOMER_AGGREGATION_DELETE_LOADING_TOOLTIP
        : CUSTOMER_AGGREGATION_DELETE_IN_USE_TOOLTIP;
    },
    isHidden: (e: ExtendedAggregationForEA) => isAggregationMarketRow(e),
  };
}

export const CUSTOMER_AGGREGATIONS_ALLOW_EDIT_ENTITY = {
  isDisabled: (e: ExtendedAggregationForEA) => isAggregationMarketRow(e),
  isHidden: (e: ExtendedAggregationForEA) => isAggregationMarketRow(e),
};

export function getCustomerAggregationsEntityForPatchPost(entity: ExtendedAggregationForEA) {
  if (isAggregationMarketRow(entity)) {
    return {
      Aggregation: entity.Aggregation,
      Market: entity.MarketAccount,
      Mode: entity.Mode ?? ModeEnum.Enabled,
    };
  }
  return {
    Name: entity.Aggregation,
    DisplayName: entity.DisplayName,
    AggregationType: entity.AggregationType ?? AggregationType.Customer,
  };
}

export const getCustomerAggregationsNewEntityDefault = () => ({
  AggregationType: AggregationType.Customer,
});

export const CUSTOMER_AGGREGATION_NAME_AND_DISPLAY_NAME_OPTIONS: InputsAndDropdownsDrawerOption<ExtendedAggregationForEA>[] =
  [
    { type: 'input', field: 'Aggregation', title: 'Name', disabledWhenEditing: true, getIsRequired: () => true },
    { type: 'input', field: 'DisplayName', title: 'Display Name', getIsRequired: () => true },
    {
      type: 'dropdown',
      field: 'AggregationType',
      title: 'Type',
      getIsDisabled: () => true,
      getDropdownOptions: () => [{ value: AggregationType.Customer, label: 'Customer' }],
      placeholder: AggregationType.Customer,
    },
  ];

export function getCustomerAggregationsTradingMarketAccountsByName(marketAccountsByName: Map<string, MarketAccount>) {
  return new Map(
    [...marketAccountsByName].filter(
      ([_, ma]) => ma.Type === MarketAccountTypeEnum.Trading && ma.Status === MarketAccountStatusEnum.Active
    )
  );
}

export function createIsMarketAccountInCustomerAggregationFilter(aggregationsWithAccounts: AggregationWithAccounts[]) {
  return (ma: MarketAccount) => {
    return aggregationsWithAccounts.some(agg => agg.Accounts.has(ma.Name));
  };
}

export function getInUseAggregationNamesSet(
  pricingRules: PricingRule[] | undefined,
  isPricingRulesLoading: boolean
): Set<string> | undefined {
  // Deletion of aggregations is unavailable until every pricing rule has loaded.
  if (isPricingRulesLoading || pricingRules == null) {
    return undefined;
  }
  return new Set(compact(pricingRules.map(rule => rule.PricingAggregation)));
}

export function getCustomerAggregationsEntityDisplayName(aggregationsByName: Map<string, Aggregation>) {
  return (e: ExtendedAggregationForEA) => aggregationsByName.get(e.Aggregation)?.DisplayName ?? e.Aggregation;
}

export function getCustomerAggregationsEntityDrawerOptions(
  aggregationMarketsDrawerOptions: InputsAndDropdownsDrawerOption<ExtendedAggregationForEA>[],
  nameAndDisplayNameDrawerOptions: InputsAndDropdownsDrawerOption<ExtendedAggregationForEA>[]
) {
  return (_: any, addingChildEntity: boolean) =>
    addingChildEntity ? aggregationMarketsDrawerOptions : nameAndDisplayNameDrawerOptions;
}

// Data filtering functions
export function getCustomerAggregationsOnly(aggregationsByName: Map<string, Aggregation>) {
  return Array.from(aggregationsByName.values()).filter(agg => agg.AggregationType === AggregationType.Customer);
}

export function getCustomerAggregationsMarketsDrawerOptionsFromContext(
  aggregationsByName: Map<string, Aggregation>,
  tradingMarketAccountsByName: Map<string, MarketAccount>,
  marketDisplayNameByName?: Map<string, string>
): InputsAndDropdownsDrawerOption<ExtendedAggregationForEA>[] {
  return getCustomerAggregationMarketsDrawerOptions(
    getCustomerAggregationsOnly(aggregationsByName),
    Array.from(tradingMarketAccountsByName.values()),
    marketDisplayNameByName
  );
}

// EntityAdmin boolean functions that use isAggregationMarketRow
export function getCustomerAggregationsShouldUsePatchForPost(e: ExtendedAggregationForEA): boolean {
  return isAggregationMarketRow(e);
}

export function getCustomerAggregationsIsRowBulkEditSelectable(e: ExtendedAggregationForEA): boolean {
  return isAggregationMarketRow(e);
}
